import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const INVOICE_NEW_PATH = "/admin-panel/registro/documento";

const estadoColor = (estado) => {
  const s = String(estado || "").toLowerCase();
  if (s.includes("pend")) return "warning";
  if (s.includes("acept")) return "success";
  if (s.includes("rech")) return "error";
  if (s.includes("factur")) return "info";
  if (s.includes("term")) return "default";
  return "default";
};

const normalizeActionLabel = (accion) => {
  const a = String(accion || "").toUpperCase();
  if (a === "ACEPTAR") return "Aceptar";
  if (a === "RECHAZAR") return "Rechazar";
  if (a === "FACTURAR") return "Facturar";
  if (a === "TERMINAR_SIN_FACTURA") return "Term. Sin Factura";
  return a;
};

const getAccessToken = () =>
  localStorage.getItem("access") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") ||
  "";

const authHeaders = (json = false) => {
  const token = getAccessToken();
  const headers = {};

  if (json) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  return headers;
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const buildInvoicePrefill = (payload) => {
  const cliente = payload?.cliente || {};
  const proforma = payload?.proforma || {};
  const rows = Array.isArray(payload?.productRows) ? payload.productRows : [];

  return {
    info: {
      cliente: cliente.cliente || "",
      identificacion_cliente: cliente.identificacion_cliente || "",
      contacto: cliente.contacto || "",
      email: cliente.email || "",
      telefono: cliente.telefono || "",
      direccion: cliente.direccion || "",
      referencia: proforma.referencia || "",
      descripcion: payload?.productDescripcion || "",
    },
    productos: {
      productos: rows.map((r) => ({
        product_id: r.product_id ?? null,
        variant_id: r.variant_id ?? null,
        code: r.code || "",
        sku: r.sku || "",
        variant_type: r.variant_type || "",
        value: r.value || "",
        codigo_principal: r.codigo_principal || "",
        codigo_auxiliar: r.codigo_auxiliar || "",
        descripcion: r.descripcion || "",
        detalles: r.detalles || "",
        cantidad: String(r.cantidad ?? "1"),
        pvp: String(r.pvp ?? "0"),
        iva_porcentaje: Number(r.iva_porcentaje ?? 0),
        descuentoPct: String(r.descuentoPct ?? "0"),
      })),
    },
    descripcion: payload?.productDescripcion || "",
    referencia: proforma.referencia || "",
    from_proforma: {
      id: proforma.id || null,
      identificacion_proforma: proforma.identificacion_proforma || "",
    },
    pagos: Array.isArray(payload?.payments) ? payload.payments : [],
  };
};

const ProformaFacturacion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [proforma, setProforma] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAccion, setConfirmAccion] = useState("");
  const [motivo, setMotivo] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  useEffect(() => {
    const token = getAccessToken();
    const isSuper = localStorage.getItem("is_superuser") === "true";
    const isAdmin = localStorage.getItem("is_admin") === "true";
    const isVendedor = localStorage.getItem("is_vendedor") === "true";

    if (!token || (!isSuper && !isAdmin && !isVendedor)) {
      navigate("/login");
    }
  }, [location, navigate]);

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        headers: authHeaders(),
      });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const fetchDetalle = async () => {
    setLoading(true);
    setError("");

    try {
      const r = await fetch(`/proformas/facturacion/${id}/`, {
        method: "GET",
        headers: authHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j.ok) {
        throw new Error(j.error || "No se pudo cargar la proforma");
      }

      setProforma(j.proforma || null);
    } catch (e) {
      setError(e?.message || "Error al cargar");
      setProforma(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchFacturaPayload = async () => {
    const r = await fetch(`/proformas/facturacion/${id}/factura-payload/`, {
      method: "GET",
      headers: authHeaders(),
    });

    if (r.status === 401 || r.status === 403) {
      localStorage.clear();
      navigate("/login");
      throw new Error("Sesión expirada");
    }

    const j = await r.json().catch(() => ({}));

    if (!r.ok || !j.ok) {
      throw new Error(j.error || "No se pudo preparar la factura");
    }

    return j.payload || j.invoice_payload || j.data || j;
  };

  useEffect(() => {
    if (!id) return;
    fetchDetalle();
  }, [id]);

  const acciones = useMemo(() => proforma?.acciones_permitidas || [], [proforma]);
  const tot = proforma?.totales || {};
  const productos = Array.isArray(proforma?.productos) ? proforma.productos : [];

  const openConfirm = (accion) => {
    setConfirmAccion(accion);
    setMotivo("");
    setConfirmOpen(true);
  };

  const doAccion = async () => {
    if (!confirmAccion) return;

    if (confirmAccion === "RECHAZAR" && !String(motivo || "").trim()) {
      setSnack({ open: true, msg: "Motivo requerido para rechazar", sev: "error" });
      return;
    }

    setSaving(true);

    try {
      const r = await fetch(`/proformas/facturacion/${id}/estado/`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          accion: confirmAccion,
          motivo: String(motivo || "").trim(),
        }),
      });

      if (r.status === 401 || r.status === 403) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j.ok) {
        throw new Error(j.error || "No se pudo cambiar el estado");
      }

      if (String(confirmAccion).toUpperCase() === "FACTURAR") {
        const payload = await fetchFacturaPayload();
        const prefillInvoice = buildInvoicePrefill(payload);

        setConfirmOpen(false);

        navigate(INVOICE_NEW_PATH, {
          state: {
            prefill_invoice: prefillInvoice,
            from_proforma_id: Number(id),
          },
        });

        return;
      }

      setSnack({ open: true, msg: "Estado actualizado", sev: "success" });
      setConfirmOpen(false);
      await fetchDetalle();
    } catch (e) {
      setSnack({ open: true, msg: e?.message || "Error al actualizar", sev: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                backgroundColor: "#1565C0",
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
              onClick={() => navigate(-1)}
            >
              Regresar
            </Button>
 
            {proforma ? (
              <Chip
                label={`Estado: ${proforma.estado_proforma || "-"}`}
                color={estadoColor(proforma.estado_proforma)}
                sx={{ fontWeight: 700 }}
              />
            ) : null}
          </Box>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1565C0", mb: 2 }}>
              Facturación de Proforma
            </Typography>

            {loading ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, fontWeight: 600 }}>Cargando…</Typography>
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : !proforma ? (
              <Alert severity="warning">No hay datos.</Alert>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 700 }}>Proforma</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {proforma.identificacion_proforma || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 700 }}>Cliente</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{proforma.cliente || "-"}</Typography>
                    <Typography sx={{ color: "#666", fontSize: 13, fontWeight: 600 }}>
                      {proforma.identificacion_cliente || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 700 }}>Contacto</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{proforma.contacto || "-"}</Typography>
                    <Typography sx={{ color: "#666", fontSize: 13, fontWeight: 600 }}>
                      {proforma.email || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 700 }}>Teléfono</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{proforma.telefono || "-"}</Typography>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography sx={{ fontWeight: 700 }}>Dirección</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{proforma.direccion || "-"}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: 700 }}>Descripción</Typography>
                    <Typography sx={{ whiteSpace: "pre-line", fontWeight: 600 }}>
                      {proforma.descripcion || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e6e6e6",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          bgcolor: "#E3F2FD",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 40,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: "#BBDEFB",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{ color: "#1565C0" }} />
                        </Box>
                        <Box>
                          <Typography sx={{ color: "#1565C0", fontWeight: 700 }}>
                            Detalles
                          </Typography>
                          <Typography sx={{ color: "#4f4f4f", fontWeight: 600, fontSize: 14 }}>
                            Información adicional de la proforma
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ whiteSpace: "pre-line", color: "#333", fontWeight: 600 }}>
                          {String(proforma.descripcion_proforma || "").trim() || "-"}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {String(proforma.motivo_rechazo || "").trim() ? (
                    <Grid item xs={12}>
                      <Alert severity="error">Motivo rechazo: {proforma.motivo_rechazo}</Alert>
                    </Grid>
                  ) : null}
                </Grid>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                    flexWrap: "wrap",
                    mb: 2,
                  }}
                >
                  {acciones.includes("ACEPTAR") ? (
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#1565C0",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "#0D47A1" },
                      }}
                      onClick={() => openConfirm("ACEPTAR")}
                      disabled={saving}
                    >
                      Aceptar
                    </Button>
                  ) : null}

                  {acciones.includes("RECHAZAR") ? (
                    <Button
                      variant="contained"
                      sx={{ bgcolor: "#d32f2f", fontWeight: 700 }}
                      onClick={() => openConfirm("RECHAZAR")}
                      disabled={saving}
                    >
                      Rechazar
                    </Button>
                  ) : null}

                  {acciones.includes("FACTURAR") ? (
                    <Button
                      startIcon={<SendRoundedIcon />}
                      variant="contained"
                      sx={{
                        bgcolor: "#1565C0",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "#0D47A1" },
                      }}
                      onClick={() => openConfirm("FACTURAR")}
                      disabled={saving}
                    >
                      Facturar
                    </Button>
                  ) : null}

                  {acciones.includes("TERMINAR_SIN_FACTURA") ? (
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: "#1565C0",
                        color: "#1565C0",
                        fontWeight: 700,
                      }}
                      onClick={() => openConfirm("TERMINAR_SIN_FACTURA")}
                      disabled={saving}
                    >
                      Term. Sin Factura
                    </Button>
                  ) : null}
                </Box>

                <Paper
                  sx={{
                    p: 0,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e6e6e6",
                  }}
                >
                  <Box sx={{ bgcolor: "#1565C0", px: 2, py: 1.25 }}>
                    <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                      Productos - Servicios
                    </Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Cant
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          P.Unit
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Desc %
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          IVA %
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {productos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography sx={{ py: 2, color: "#666", fontWeight: 600 }}>
                              Sin productos
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        productos.map((it) => (
                          <TableRow key={it.id}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700 }}>
                                {it.codigo_principal || "-"}
                              </Typography>
                              {String(it.codigo_auxiliar || "").trim() ? (
                                <Typography sx={{ color: "#666", fontSize: 12, fontWeight: 600 }}>
                                  - {it.codigo_auxiliar}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "pre-line", fontWeight: 600 }}>
                              {it.descripcion || ""}
                              {String(it.detalles || "").trim() ? `\n- ${it.detalles}` : ""}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {Number(it.cantidad_producto || 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatMoney(it.precio_producto)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {Number(it.descuento_pct || 0).toFixed(2)}%
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {Number(it.iva_porcentaje || 0).toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Paper sx={{ p: 2, width: 380, maxWidth: "100%", border: "1px solid #e6e6e6" }}>
                    {[
                      ["Subtotal 15%", tot.subtotal15],
                      ["Subtotal 5%", tot.subtotal5],
                      ["Subtotal 0%", tot.subtotal0],
                      ["Subtotal No Obj", tot.subtotal_no_obj],
                      ["Descuento", tot.descuento_total],
                      ["IVA 15%", tot.iva15],
                      ["IVA 5%", tot.iva5],
                      ["Total", tot.total],
                    ].map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}
                      >
                        <Typography sx={{ fontWeight: label === "Total" ? 700 : 600 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontWeight: label === "Total" ? 700 : 600 }}>
                          {formatMoney(value)}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={saving ? undefined : () => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
            ¿Seguro que desea {normalizeActionLabel(confirmAccion)} esta proforma?
          </Typography>

          {confirmAccion === "RECHAZAR" ? (
            <TextField
              label="Motivo de rechazo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          ) : null}

          {saving ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <CircularProgress size={22} />
              <Typography sx={{ fontWeight: 600 }}>Procesando…</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving} sx={{ fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={doAccion}
            disabled={saving}
            sx={{ bgcolor: "#1565C0", fontWeight: 700 }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {snack.sev === "error" ? "Error" : "Listo"}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity={snack.sev} sx={{ mb: 0 }}>
            {snack.msg}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSnack((s) => ({ ...s, open: false }))} sx={{ fontWeight: 700 }}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProformaFacturacion;